import { useEffect } from 'react';
import type { LogError, NativeMessage } from './contracts';
import IncomingMessage from './IncomingMessage';
import MessageHandler from './MessageHandler';
import safeParse from './utils/safeParse';
import toError from './utils/toError';

function maybeStringToObject(data: unknown): unknown {
  return typeof data === 'string' ? safeParse(data) : data;
}

function hasSourceProperty(value: unknown): value is { source: unknown } {
  return typeof value === 'object' && value != null && 'source' in value;
}

function isNativeMessage(value: unknown): value is NativeMessage<any, any> {
  return (
    hasSourceProperty(value) &&
    (value.source === 'native_android' || value.source === 'native_ios')
  );
}

function isKnownNativeMessage<Event>(
  knownEvents: Record<string, Event>,
  message: NativeMessage<any, any>
): message is NativeMessage<Event, any> {
  return Object.values(knownEvents).includes(message.event);
}

function shouldBeHandled<Event>(
  eventsToHandle: Partial<Record<string, Event>>,
  message: NativeMessage<any, any>
): message is NativeMessage<Event, any> {
  return Object.values(eventsToHandle).includes(message.event);
}

type Options<Event> = {
  eventsToHandle?: Partial<Record<string, Event>>;
  handlers: MessageHandler<Event>[];
  knownEvents: Record<string, Event>;
  logErrorToTelemetry: LogError;
  logErrorToUser?: (error: Error) => void;
  verboseLogs?: boolean;
};

/**
 * This hook listens for messages from the native app.
 *
 * @example
 * import {
 *   type MessageHandler,
 *   useNativeMessageListener as useNativeMessagePackageListener,
 * } from '@carvana/vdp-native-hooks';
 * import logError from '~/utilities/logError';
 * import Event from './handlers/event';
 *
 * export default function useNativeMessageListener() {
 *   const addFavoriteHandler = useAddFavoriteHandler();
 *
 *   useNativeMessagePackageListener<Event>({
 *     handlers: useMemo<Array<MessageHandler<Event>>>(
 *       () => [
 *         addFavoriteHandler
 *       ],
 *       [
 *         addFavoriteHandler
 *       ]
 *     ),
 *     logErrorToTelemetry: logError,
 *     verboseLogs: process.env.DEPLOY_ENV !== 'prod'
 *   });
 * }
 */

export default function useNativeMessageListener<Event>({
  handlers,
  knownEvents,
  eventsToHandle = knownEvents,
  logErrorToTelemetry,
  logErrorToUser,
  verboseLogs
}: Options<Event>) {
  useEffect(() => {
    const handlePostMessage = (ev: MessageEvent<unknown>) => {
      const message = maybeStringToObject(ev.data);

      if (!isNativeMessage(message)) return;

      if (verboseLogs) {
        console.debug('Message arrived', message);
      }

      if (!isKnownNativeMessage(knownEvents, message)) {
        logErrorToTelemetry(new Error('Unknown native event'), { message });

        return;
      }

      // this must go after check for isKnownNativeMessage to ensure we are still capturing unknown events
      if (!shouldBeHandled(eventsToHandle, message)) return;

      const incomingMessage = new IncomingMessage<Event, any>(
        message,
        handlers
      );

      try {
        incomingMessage.handleMessage();
      } catch (err) {
        logErrorToTelemetry(toError(err), {
          location: 'Native message handler'
        });
        if (logErrorToUser) logErrorToUser(toError(err));
      }

      if (!incomingMessage.wasHandled) {
        logErrorToTelemetry(new Error('Unhandled native event'), { message });
      }
    };

    window.addEventListener('message', handlePostMessage, false);

    return () => {
      window.removeEventListener('message', handlePostMessage, false);
    };
  }, [
    eventsToHandle,
    handlers,
    knownEvents,
    logErrorToTelemetry,
    logErrorToUser,
    verboseLogs
  ]);
}
